import React, { useState } from 'react'
import { useIsActive } from 'shared/components/header/hooks/use-is-active'
import {
  MenuItemInterface,
  SingleItemDropdownInterface,
  SingleItemHrefInterface,
} from 'shared/components/header/types/menu-interface'
import { isFlatMenuItem } from 'shared/components/header/utils/is-flat-menu-item'
import { replaceHelpHref } from 'shared/components/header/utils/replace-help-href'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import useUser from 'shared/hooks/use-user'
import { ChevronDownIcon } from '@systemeio/ui'

const SideMenuItem = ({ item }: MenuItemInterface) =>
  isFlatMenuItem(item) ? <SideMenuItemFlat {...item} /> : <SideMenuDropdown {...item} />

export default SideMenuItem

export function SideMenuItemFlat(item: SingleItemHrefInterface) {
  const isActive = useIsActive(item)

  const { user } = useUser()

  if (!user) return null

  return (item.showForRoles || user.roles).some(role => user.roles.includes(role)) ? (
    <div className="group relative inline-block transition-all">
      <div
        className={`flex items-center gap-4 ${
          isActive && '[&>svg]:fill-brand-primary'
        } [&>svg]:w-[24px]`}
      >
        {item.mobileIconRenderer && item.mobileIconRenderer()}
        <LinkWithoutPrefetch
          className={`${
            isActive ? 'text-brand-primary' : 'text-text-primary'
          } inline-flex grow items-center text-lg font-bold tracking-wide`}
          href={replaceHelpHref(item.href, user.dashboardLocale)}
          target={item.target}
        >
          {item.name}
        </LinkWithoutPrefetch>
      </div>
    </div>
  ) : null
}

function SideMenuDropdown(item: SingleItemDropdownInterface) {
  const { user } = useUser()
  const isActive = useIsActive(item)
  const [open, setOpen] = useState(false)
  const dropdownItems = item.dropdownItems
  if (dropdownItems.length === 0) return null
  if (!user) return null

  return (
    <div>
      <div
        className={`flex cursor-pointer items-center gap-4 ${
          isActive && '[&>svg]:fill-brand-primary'
        } [&>svg]:w-[24px]`}
        onClick={() => setOpen(prev => !prev)}
      >
        {item.mobileIconRenderer && item.mobileIconRenderer()}
        <div
          className={`${
            isActive ? 'text-brand-primary' : 'text-text-primary'
          } inline-flex grow items-center text-lg font-bold tracking-wide`}
        >
          {item.name}
          <ChevronDownIcon
            className={`ml-auto self-center transition-transform ${open && 'rotate-180'}`}
            width={15}
            height={5}
          />
        </div>
      </div>
      {open && <SideMenuDropdownPanel dropdownItems={dropdownItems} />}
    </div>
  )
}

const SideMenuDropdownPanel = ({
  dropdownItems,
}: Pick<SingleItemDropdownInterface, 'dropdownItems'>) => {
  const { user, isWsAssistant } = useUser()
  if (!user) return null
  return (
    <div className="flex flex-col gap-2 whitespace-nowrap pl-10 pt-2 text-sm">
      {dropdownItems.map(item => {
        if (isFlatMenuItem(item)) {
          return (
            (item.showForRoles || user.roles).some(role => user.roles.includes(role)) && (
              <SideMenuSubItem
                {...item}
                href={isWsAssistant ? item.assistantLink || item.href : item.href}
                key={item.href}
              />
            )
          )
        } else {
          return <SideSubMenu key={item.name} {...item} />
        }
      })}
    </div>
  )
}

const SideSubMenu = (item: SingleItemDropdownInterface) => {
  const [open, setOpen] = useState(false)
  const isActive = useIsActive(item)
  const dropdownItems = item.dropdownItems
  if (dropdownItems.length === 0) return null
  return (
    <div>
      <div
        className={`flex cursor-pointer items-center gap-4 ${
          isActive && '[&>svg]:fill-brand-primary'
        } [&>svg]:w-[24px]`}
        onClick={() => setOpen(prev => !prev)}
      >
        {item.mobileIconRenderer && item.mobileIconRenderer()}
        <div className={getSubItemClass(isActive)}>
          {item.name}
          <ChevronDownIcon
            className={`ml-auto self-center transition-transform ${open && 'rotate-180'}`}
            width={15}
            height={5}
          />
        </div>
      </div>
      {open && <SideMenuDropdownPanel dropdownItems={dropdownItems} />}
    </div>
  )
}

function SideMenuSubItem(item: SingleItemHrefInterface) {
  const isActive = useIsActive(item)
  return (
    <LinkWithoutPrefetch className={getSubItemClass(isActive)} href={item.href}>
      {item.name}
    </LinkWithoutPrefetch>
  )
}

const getSubItemClass = (isActive: boolean) =>
  `${
    isActive ? 'text-brand-primary' : 'text-text-primary'
  } mb-1 hover:text-brand-primary text-[15px] font-light flex items-center justify-between w-full main-transition-colors`
